/* Org picker (/orgs) realtime sync — inventory-parity branding path.
 *
 * Listens for `tenant_branding_updated` on the shared `websocket-message`
 * bus (fed by usePublicDirectorySSE) and coalesces invalidation of public
 * customer-tenants queries so Vzhled logo/brand-kit changes appear without F5.
 * Also prefix-invalidates salesPointContacts (G5) so warm Oblíbené thumbs
 * refresh after leaving /orgs. Guest Oblíbené pending LS branding re-merges via
 * shops sales-points invalidate (G8) — this hook keeps /orgs tenant thumbs live.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createCoalescedRefetchScheduler,
  type CoalescedRefetchScheduler,
} from 'pi-kiosk-shared';
import {
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import {
  invalidateAllAccountMembershipsCache,
  invalidateAllKioskPickupPointsCache,
  invalidateAllPromoTenantQueriesCache,
  invalidateAllSalesPointContactsCache,
  invalidateAllSurfaceEntitlementCache,
} from '../../../shared/api/customerDirectoryCacheInvalidate.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import {
  DIRECTORY_STALE_TIME_SSE_MS,
  SSE_DIRECTORY_REFRESH_COOLDOWN_MS,
} from '../../shops/hooks/useShopsDirectoryRealtimeSync.js';
import { usePublicDirectorySseConnected } from '../../shops/hooks/PublicDirectorySseProvider.js';

/** Poll when public directory SSE is down — `/orgs` picker. */
export const ORG_DIRECTORY_REFETCH_INTERVAL_MS = 30_000;

/** Higher staleTime while public directory SSE is live (shops/catalog parity). */
export { DIRECTORY_STALE_TIME_SSE_MS };

export function orgDirectoryListRefetchInterval(sseConnected: boolean): number | false {
  return sseConnected ? false : ORG_DIRECTORY_REFETCH_INTERVAL_MS;
}

export function orgDirectoryStaleTime(sseConnected: boolean): number {
  return sseConnected ? DIRECTORY_STALE_TIME_SSE_MS : 60_000;
}

/** Shared React Query options for `/orgs` + public customer-tenants consumers (Obchody parity). */
export function orgDirectoryQueryOptions(sseConnected: boolean): {
  staleTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: true;
} {
  return {
    staleTime: orgDirectoryStaleTime(sseConnected),
    refetchInterval: orgDirectoryListRefetchInterval(sseConnected),
    // After admin bank verify in another tab, refetch when customer tab regains focus.
    refetchOnWindowFocus: true,
  };
}

interface BrandingUpdateMessage {
  type?: string;
  data?: Record<string, unknown>;
}

function isCustomerDirectoryUpdated(message: BrandingUpdateMessage): boolean {
  if (message.type === 'customer_directory_updated') {
    return true;
  }
  const nestedType = message.data?.type;
  return nestedType === 'customer_directory_updated';
}

function isTenantBrandingUpdated(message: BrandingUpdateMessage): boolean {
  if (message.type === 'tenant_branding_updated') {
    return true;
  }
  const nestedType = message.data?.type;
  return nestedType === 'tenant_branding_updated';
}

function isTenantPromotionsConfigUpdated(message: BrandingUpdateMessage): boolean {
  if (message.type === 'tenant_promotions_config_updated') {
    return true;
  }
  const nestedType = message.data?.type;
  return nestedType === 'tenant_promotions_config_updated';
}

export function useOrgDirectoryRealtimeSync(): boolean {
  const queryClient = useQueryClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const queryClientRef = useRef(queryClient);
  const apiBaseUrlRef = useRef(apiBaseUrl);
  const coalescedRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const lastSseOrgRefreshRef = useRef(0);
  const stormCatchUpPendingRef = useRef(false);

  const coalescedSalesPointsRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);

  useEffect(() => {
    queryClientRef.current = queryClient;
    apiBaseUrlRef.current = apiBaseUrl;
  }, [queryClient, apiBaseUrl]);

  useEffect(() => {
    if (coalescedRefetchRef.current === null) {
      coalescedRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => customerQueryKeys.publicCustomerTenants(apiBaseUrlRef.current),
      );
    }
    if (coalescedSalesPointsRefetchRef.current === null) {
      coalescedSalesPointsRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => ['public', 'customer-sales-points', apiBaseUrlRef.current],
      );
    }
    const coalescedRefetch = coalescedRefetchRef.current;
    const coalescedSalesPointsRefetch = coalescedSalesPointsRefetchRef.current;
    return (): void => {
      coalescedRefetch.cancel();
      coalescedSalesPointsRefetch.cancel();
    };
  }, []);

  useEffect(() => {
    if (isHttpStormLatched()) {
      stormCatchUpPendingRef.current = true;
    }
    return subscribeHttpStormLatch(() => {
      if (isHttpStormLatched()) {
        stormCatchUpPendingRef.current = true;
      }
    });
  }, []);

  const scheduleOrgRefetch = useCallback((): void => {
    coalescedRefetchRef.current?.schedule();
    coalescedSalesPointsRefetchRef.current?.schedule();
  }, []);

  const isConnected = usePublicDirectorySseConnected();

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    const now = Date.now();
    const stormBypass = stormCatchUpPendingRef.current;
    if (stormBypass) {
      stormCatchUpPendingRef.current = false;
    } else if (now - lastSseOrgRefreshRef.current < SSE_DIRECTORY_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastSseOrgRefreshRef.current = now;
    scheduleOrgRefetch();
  }, [isConnected, scheduleOrgRefetch]);

  useEffect(() => {
    const handleWebSocketMessage = (event: Event): void => {
      try {
        const customEvent = event as CustomEvent<{ data: string }>;
        const raw = customEvent.detail?.data;
        if (typeof raw !== 'string' || raw.length === 0) {
          return;
        }
        const message = JSON.parse(raw) as BrandingUpdateMessage;
        if (
          !isTenantBrandingUpdated(message) &&
          !isCustomerDirectoryUpdated(message) &&
          !isTenantPromotionsConfigUpdated(message)
        ) {
          return;
        }
        // G5 — Oblíbené/contacts thumbs warm while on /orgs; same helper as shops.
        invalidateAllSalesPointContactsCache(queryClientRef.current);
        // G9 — memberships DTO carries logoUrl + rim; no tenant/customer in scope.
        invalidateAllAccountMembershipsCache(queryClientRef.current);
        // G12 — entitlements + checkout pickup + promo tenant caches.
        invalidateAllSurfaceEntitlementCache(queryClientRef.current);
        invalidateAllKioskPickupPointsCache(queryClientRef.current);
        invalidateAllPromoTenantQueriesCache(queryClientRef.current);
        scheduleOrgRefetch();
      } catch {
        return;
      }
    };

    const handleForceRefresh = (): void => {
      scheduleOrgRefetch();
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);
    window.addEventListener('force-refresh', handleForceRefresh);

    return (): void => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
      window.removeEventListener('force-refresh', handleForceRefresh);
    };
  }, [scheduleOrgRefetch]);

  return isConnected;
}
